import { PlanTierIndex, useRestApiProvider } from "@jugl-web/rest-api";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models/TicketDetails";
import { Avatar, DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useAppVariant } from "@jugl-web/utils";
import { getContactChannelConnectionStatus } from "@jugl-web/domain-resources/contacts/utils";
import { getPlanTierIndex } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useMemo } from "react";
import {
  CustomerOtherTicketsSection,
  CustomerProfileSections,
} from "../../../CustomerInfo";

export type CustomerTabContentProps = {
  ticket: TicketDetailsModel;
  rootTicketId?: string;
  onTicketLookup: (ticketId: string) => void;
};

export const CustomerTabContent: FC<CustomerTabContentProps> = ({
  ticket,
  rootTicketId,
  onTicketLookup,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { contactsApi, integrationsApi } = useRestApiProvider();
  const { currentPlan } = useSubscriptionV2();
  const { isCx } = useAppVariant();

  const contactId = ticket.contact.id;

  const {
    data: contact,
    isFetching: isContactFetching,
    isError: isContactError,
    refetch: refetchContact,
  } = contactsApi.useGetContactByContactIdQuery({
    entityId,
    contactId,
    meta: { silent: true },
  });

  const {
    data: crmConfig,
    isLoading: isCrmConfigLoading,
    isError: isCrmConfigError,
    refetch: refetchCrmConfig,
  } = integrationsApi.useGetCRMConfigQuery({ entityId });

  const contactName = contact?.name || ticket.contact.name || "";

  const channelConnectionStatus = useMemo(
    () =>
      crmConfig ? getContactChannelConnectionStatus(crmConfig) : undefined,
    [crmConfig]
  );

  const hasBusinessPlanOrAbove = useMemo(
    () =>
      currentPlan && getPlanTierIndex(currentPlan) >= PlanTierIndex.business,
    [currentPlan]
  );

  // Exclude the current ticket and the root ticket (if nested) from the list of other tickets
  const ticketIdsToExclude = useMemo(() => {
    const ids = [ticket.id];

    if (rootTicketId) {
      ids.push(rootTicketId);
    }

    return ids;
  }, [ticket.id, rootTicketId]);

  return (
    <DataLoadingWrapper
      isLoading={isContactFetching || isCrmConfigLoading}
      isError={isContactError || isCrmConfigError}
      onRetry={() => {
        if (isContactError) {
          refetchContact();
        }

        if (isCrmConfigError) {
          refetchCrmConfig();
        }
      }}
    >
      <div className="jugl__custom-scrollbar min-h-0 flex-1 overflow-auto py-8">
        <div className="flex items-center justify-between gap-4 px-8">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar size="md" username={contactName} />
            <span className="truncate font-secondary text-lg leading-[140%] font-semibold tracking-[0.18px] text-dark">
              {contactName}
            </span>
          </div>
        </div>
        <div className="my-7 h-px w-full shrink-0 bg-[#F2F2F2]" />
        {contact && (
          <CustomerProfileSections
            contact={contact}
            channelConnectionStatus={channelConnectionStatus}
            isVisitorIntelligenceVisible={!!hasBusinessPlanOrAbove && isCx}
          />
        )}
        <CustomerOtherTicketsSection
          ticketIdsToExclude={ticketIdsToExclude}
          contactId={contactId}
          onTicketClick={onTicketLookup}
        />
      </div>
    </DataLoadingWrapper>
  );
};
